import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { getApiErrorMessage, orgApi } from '.././api';

const REQUIRED_REFERRAL_CODE = 'ZILVERRA@0088';

const PageShell = styled.div`
  min-height: 100vh;
  padding: 32px;
  background:
    radial-gradient(circle at top left, rgba(184, 134, 11, 0.18), transparent 28%),
    radial-gradient(circle at right center, rgba(15, 23, 42, 0.1), transparent 24%),
    linear-gradient(135deg, #f8f5ef 0%, #fffdf8 52%, #f2ede4 100%);
  color: #1f2937;
`;

const Layout = styled.div`
  width: 100%;
  max-width: 1360px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 28px;

  @media (max-width: 1080px) {
    grid-template-columns: 1fr;
  }
`;

const SidePanel = styled.aside`
  position: sticky;
  top: 24px;
  align-self: start;
  padding: 28px;
  border-radius: 28px;
  background: linear-gradient(180deg, #111827 0%, #1f2937 100%);
  color: #f9fafb;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);

  @media (max-width: 1080px) {
    position: static;
  }
`;

const SideEyebrow = styled.div`
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: rgba(245, 208, 84, 0.9);
  margin-bottom: 12px;
`;

const SideTitle = styled.h1`
  margin: 0;
  font-size: 38px;
  line-height: 1.05;
  color: #ffffff;
`;

const SideText = styled.p`
  margin: 16px 0 0;
  color: rgba(229, 231, 235, 0.84);
  line-height: 1.7;
  font-size: 14px;
`;

const StepRail = styled.div`
  margin-top: 28px;
  display: grid;
  gap: 14px;
`;

const StepCard = styled.div`
  padding: 16px 18px;
  border-radius: 18px;
  border: 1px solid ${props => props.active ? 'rgba(245, 208, 84, 0.7)' : 'rgba(255, 255, 255, 0.08)'};
  background: ${props => props.active ? 'rgba(245, 208, 84, 0.12)' : 'rgba(255, 255, 255, 0.04)'};
  opacity: ${props => (props.active || props.done ? 1 : 0.72)};
`;

const StepMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const StepIndex = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  font-weight: 700;
  font-size: 14px;
  background: ${props => props.done ? '#f5d054' : props.active ? '#ffffff' : 'rgba(255,255,255,0.12)'};
  color: ${props => props.done ? '#111827' : props.active ? '#111827' : '#f9fafb'};
`;

const StepTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  color: #ffffff;
`;

const StepDesc = styled.div`
  margin-top: 6px;
  font-size: 12px;
  line-height: 1.5;
  color: rgba(229, 231, 235, 0.72);
`;

const SummaryCard = styled.div`
  margin-top: 24px;
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const SummaryTitle = styled.div`
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: rgba(245, 208, 84, 0.84);
`;

const SummaryValue = styled.div`
  margin-top: 10px;
  font-size: 14px;
  color: #f9fafb;
  line-height: 1.6;
`;

const MainPanel = styled.section`
  padding: 30px;
  border-radius: 30px;
  background: rgba(255, 255, 255, 0.86);
  border: 1px solid rgba(255, 255, 255, 0.72);
  box-shadow: 0 26px 60px rgba(148, 163, 184, 0.18);
  backdrop-filter: blur(16px);
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px;
  margin-bottom: 26px;

  @media (max-width: 720px) {
    flex-direction: column;
  }
`;

const HeaderTitle = styled.h2`
  margin: 0;
  font-size: 34px;
  line-height: 1.1;
  color: #111827;
`;

const HeaderText = styled.p`
  margin: 10px 0 0;
  max-width: 760px;
  color: #4b5563;
  line-height: 1.7;
  font-size: 15px;
`;

const MandatoryNote = styled.div`
  padding: 12px 14px;
  border-radius: 14px;
  background: #fff8e6;
  border: 1px solid #f3d27a;
  color: #7c5b00;
  font-size: 13px;
  font-weight: 600;
`;

const ErrorBanner = styled.div`
  margin-bottom: 22px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid #fecaca;
  background: #fff1f2;
  color: #b91c1c;
  font-size: 14px;
`;

const SuccessBanner = styled.div`
  margin-bottom: 22px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid #bbf7d0;
  background: #f0fdf4;
  color: #15803d;
  font-size: 14px;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(12, minmax(0, 1fr));
  gap: 18px;
`;

const SectionHeading = styled.div`
  grid-column: 1 / -1;
  margin: 14px 0 2px;
  padding-top: 6px;
  border-top: 1px solid #e5e7eb;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  color: #111827;
`;

const SectionText = styled.p`
  margin: 6px 0 0;
  color: #6b7280;
  font-size: 13px;
`;

const Field = styled.div`
  grid-column: span ${props => props.span || 12};

  @media (max-width: 980px) {
    grid-column: span 12;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 13px;
  font-weight: 700;
  color: #1f2937;
`;

const RequiredMark = styled.span`
  color: #b91c1c;
`;

const Input = styled.input`
  width: 100%;
  min-height: 52px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid ${props => props.invalid ? '#fca5a5' : '#d1d5db'};
  background: ${props => props.readOnly ? '#f8fafc' : '#ffffff'};
  color: #111827;
  font-size: 15px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #b8860b;
    box-shadow: 0 0 0 4px rgba(184, 134, 11, 0.12);
    transform: translateY(-1px);
  }
`;

const Helper = styled.div`
  margin-top: 7px;
  font-size: 12px;
  color: ${props => props.invalid ? '#b91c1c' : '#6b7280'};
`;

const ReferralPanel = styled.div`
  margin-top: 6px;
  padding: 18px;
  border-radius: 20px;
  border: 1px solid ${props => props.valid ? '#bbf7d0' : '#f3d27a'};
  background: ${props => props.valid ? '#f0fdf4' : '#fff8e6'};
`;

const ReferralStatus = styled.div`
  margin-top: 10px;
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.valid ? '#15803d' : '#9a6700'};
`;

const ActionBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-top: 28px;
  padding-top: 22px;
  border-top: 1px solid #e5e7eb;

  @media (max-width: 720px) {
    flex-direction: column-reverse;
    align-items: stretch;
  }
`;

const ActionNote = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;

  @media (max-width: 720px) {
    display: grid;
    grid-template-columns: 1fr;
  }
`;

const Button = styled.button`
  min-height: 52px;
  padding: 0 20px;
  border-radius: 16px;
  border: 1px solid ${props => props.primary ? '#111827' : '#d1d5db'};
  background: ${props => props.primary ? 'linear-gradient(135deg, #111827 0%, #b8860b 100%)' : '#ffffff'};
  color: ${props => props.primary ? '#ffffff' : '#111827'};
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.01em;
  cursor: pointer;
  transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
  opacity: ${props => props.disabled ? 0.58 : 1};
  box-shadow: ${props => props.primary ? '0 18px 34px rgba(17, 24, 39, 0.16)' : 'none'};

  &:hover {
    transform: ${props => props.disabled ? 'none' : 'translateY(-1px)'};
  }
`;

const VerifyWrap = styled.div`
  display: grid;
  justify-items: center;
  gap: 14px;
  padding: 54px 16px 26px;
  text-align: center;
`;

const VerifyBadge = styled.div`
  width: 78px;
  height: 78px;
  border-radius: 24px;
  display: grid;
  place-items: center;
  font-size: 26px;
  font-weight: 800;
  color: #111827;
  background: linear-gradient(135deg, #f5d054 0%, #f8fafc 100%);
  border: 1px solid rgba(184, 134, 11, 0.45);
`;

const VerifyTitle = styled.h3`
  margin: 0;
  font-size: 28px;
  color: #111827;
`;

const VerifyText = styled.p`
  margin: 0;
  max-width: 560px;
  color: #4b5563;
  line-height: 1.7;
`;

const steps = [
  { title: 'Organization', description: 'Brand identity, company basics, and financial defaults.' },
  { title: 'Location', description: 'Primary address and branch communication details.' },
  { title: 'Admin Access', description: 'Admin account, referral validation, and account security.' },
  { title: 'Verify Email', description: 'Confirm the admin email before the first login.' },
];

const SetupWizard = ({ onFinalize }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [pwVisible, setPwVisible] = useState(false);

  const today = new Date();
  const nextYear = new Date();
  nextYear.setFullYear(today.getFullYear() + 1);

  const [formData, setFormData] = useState({
    orgName: '',
    legalName: '',
    domain: '',
    phone: '',
    country: 'India',
    timezone: 'Asia/Kolkata',
    currency: 'INR',
    gstin: '',
    pan: '',
    fiscalStart: today.toISOString().split('T')[0],
    fiscalEnd: nextYear.toISOString().split('T')[0],
    address: '',
    city: '',
    state: '',
    pincode: '',
    branchPhone: '',
    adminEmail: '',
    adminPassword: '',
    firstName: '',
    lastName: '',
    adminMobile: '',
    referralCode: '',
  });

  const adminUsername = useMemo(() => (formData.adminEmail || '').trim().toLowerCase(), [formData.adminEmail]);
  const referralCodeValid = formData.referralCode.trim() === REQUIRED_REFERRAL_CODE;

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'adminEmail' ? value.trim().toLowerCase() : value,
    }));
  };

  const validateStep = (targetStep = step) => {
    const trimmed = {
      orgName: formData.orgName.trim(),
      legalName: formData.legalName.trim(),
      address: formData.address.trim(),
      city: formData.city.trim(),
      state: formData.state.trim(),
      pincode: formData.pincode.trim(),
      adminEmail: formData.adminEmail.trim(),
      firstName: formData.firstName.trim(),
      adminPassword: formData.adminPassword,
      referralCode: formData.referralCode.trim(),
    };

    if (targetStep === 1) {
      if (!trimmed.orgName) return 'Organization name is required.';
      if (!trimmed.legalName) return 'Legal name is required.';
      if (!formData.fiscalStart || !formData.fiscalEnd) return 'Fiscal start and fiscal end are required.';
    }

    if (targetStep === 2) {
      if (!trimmed.address) return 'Street address is required.';
      if (!trimmed.city) return 'City is required.';
      if (!trimmed.state) return 'State is required.';
      if (!trimmed.pincode) return 'Pincode is required.';
    }

    if (targetStep === 3) {
      if (!trimmed.firstName) return 'Admin first name is required.';
      if (!trimmed.adminEmail) return 'Admin email is required.';
      if (!/\S+@\S+\.\S+/.test(trimmed.adminEmail)) return 'Enter a valid admin email address.';
      if (!trimmed.adminPassword || trimmed.adminPassword.length < 8) return 'Password must be at least 8 characters.';
      if (!trimmed.referralCode) return 'Referral code is required.';
      if (!referralCodeValid) return 'Referral code must match the approved organization referral code.';
    }

    return '';
  };

  const goToNextStep = () => {
    const validationMessage = validateStep(step);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }
    setError('');
    setStep((prev) => Math.min(prev + 1, 4));
  };

  const handleRegister = async () => {
    const validationMessage = validateStep(3);
    if (validationMessage) {
      setError(validationMessage);
      return;
    }

    setError('');
    setLoading(true);

    const payload = {
      organization: {
        name: formData.orgName.trim(),
        legal_name: formData.legalName.trim(),
        domain: formData.domain.trim(),
        phone: formData.phone.trim(),
        country: formData.country,
        timezone: formData.timezone,
        currency: formData.currency,
        gstin: formData.gstin.trim(),
        pan: formData.pan.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        pincode: formData.pincode.trim(),
        fiscal_year_start: formData.fiscalStart,
        fiscal_year_end: formData.fiscalEnd,
        is_active: true,
      },
      admin_user: {
        email: formData.adminEmail.trim().toLowerCase(),
        username: adminUsername,
        password: formData.adminPassword,
        mobile: formData.adminMobile.trim(),
        first_name: formData.firstName.trim(),
        last_name: formData.lastName.trim(),
      },
      default_branch: {
        name: 'Head Office',
        address: formData.address.trim(),
        city: formData.city.trim(),
        state: formData.state.trim(),
        pincode: formData.pincode.trim(),
        phone: formData.branchPhone.trim() || formData.phone.trim(),
        branch_type: 'head_office',
        approval_for_inter_branch_transfer: true,
      },
      referral_code: formData.referralCode.trim(),
      create_default_branch: true,
    };

    try {
      await orgApi.post('/orgs/register', payload);
      setStep(4);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Registration failed.'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError('');
    if (!otpCode.trim()) {
      setError('Please enter the OTP sent to the admin email.');
      return;
    }

    setLoading(true);
    try {
      await orgApi.post('/auth/email/verify', {
        email: formData.adminEmail.trim().toLowerCase(),
        otp_code: otpCode.trim(),
      });
      if (onFinalize) onFinalize();
    } catch (err) {
      setError(getApiErrorMessage(err, 'Invalid OTP.'));
    } finally {
      setLoading(false);
    }
  };

  const renderStep1 = () => (
    <SectionGrid>
      <SectionHeading>
        <SectionTitle>Organization identity</SectionTitle>
        <SectionText>Set up the company record that will anchor sales, production, and compliance.</SectionText>
      </SectionHeading>

      <Field span={6}>
        <Label>Organization Name <RequiredMark>*</RequiredMark></Label>
        <Input name="orgName" value={formData.orgName} onChange={handleChange} placeholder="Acme Jewels" />
      </Field>
      <Field span={6}>
        <Label>Legal Name <RequiredMark>*</RequiredMark></Label>
        <Input name="legalName" value={formData.legalName} onChange={handleChange} placeholder="Acme Jewels Private Limited" />
      </Field>
      <Field span={6}>
        <Label>Domain / Website</Label>
        <Input name="domain" value={formData.domain} onChange={handleChange} placeholder="acmejewels.com" />
      </Field>
      <Field span={3}>
        <Label>Country</Label>
        <Input name="country" value={formData.country} onChange={handleChange} />
      </Field>
      <Field span={3}>
        <Label>Timezone</Label>
        <Input name="timezone" value={formData.timezone} onChange={handleChange} />
      </Field>

      <SectionHeading>
        <SectionTitle>Tax and finance</SectionTitle>
        <SectionText>These values help initialize accounting and invoicing defaults.</SectionText>
      </SectionHeading>

      <Field span={3}>
        <Label>GSTIN</Label>
        <Input name="gstin" value={formData.gstin} onChange={handleChange} placeholder="GST number" />
      </Field>
      <Field span={3}>
        <Label>PAN</Label>
        <Input name="pan" value={formData.pan} onChange={handleChange} placeholder="PAN number" />
      </Field>
      <Field span={2}>
        <Label>Currency</Label>
        <Input name="currency" value={formData.currency} onChange={handleChange} />
      </Field>
      <Field span={2}>
        <Label>Fiscal Start <RequiredMark>*</RequiredMark></Label>
        <Input type="date" name="fiscalStart" value={formData.fiscalStart} onChange={handleChange} />
      </Field>
      <Field span={2}>
        <Label>Fiscal End <RequiredMark>*</RequiredMark></Label>
        <Input type="date" name="fiscalEnd" value={formData.fiscalEnd} onChange={handleChange} />
      </Field>
    </SectionGrid>
  );

  const renderStep2 = () => (
    <SectionGrid>
      <SectionHeading>
        <SectionTitle>Head office location</SectionTitle>
        <SectionText>This becomes the default branch address during organization creation.</SectionText>
      </SectionHeading>

      <Field span={12}>
        <Label>Street Address <RequiredMark>*</RequiredMark></Label>
        <Input name="address" value={formData.address} onChange={handleChange} placeholder="Full office address" />
      </Field>
      <Field span={4}>
        <Label>City <RequiredMark>*</RequiredMark></Label>
        <Input name="city" value={formData.city} onChange={handleChange} />
      </Field>
      <Field span={4}>
        <Label>State <RequiredMark>*</RequiredMark></Label>
        <Input name="state" value={formData.state} onChange={handleChange} />
      </Field>
      <Field span={4}>
        <Label>Pincode <RequiredMark>*</RequiredMark></Label>
        <Input name="pincode" value={formData.pincode} onChange={handleChange} />
      </Field>

      <SectionHeading>
        <SectionTitle>Contact channels</SectionTitle>
        <SectionText>Use the main number for organization contact and a branch-specific number if needed.</SectionText>
      </SectionHeading>

      <Field span={6}>
        <Label>Official Phone</Label>
        <Input name="phone" value={formData.phone} onChange={handleChange} placeholder="Main office line" />
      </Field>
      <Field span={6}>
        <Label>Branch Phone</Label>
        <Input name="branchPhone" value={formData.branchPhone} onChange={handleChange} placeholder="Head office branch line" />
      </Field>
    </SectionGrid>
  );

  const renderStep3 = () => (
    <SectionGrid>
      <SectionHeading>
        <SectionTitle>Admin account</SectionTitle>
        <SectionText>The admin email must be verified before the admin can log in. Staff login is unchanged.</SectionText>
      </SectionHeading>

      <Field span={6}>
        <Label>First Name <RequiredMark>*</RequiredMark></Label>
        <Input name="firstName" value={formData.firstName} onChange={handleChange} />
      </Field>
      <Field span={6}>
        <Label>Last Name</Label>
        <Input name="lastName" value={formData.lastName} onChange={handleChange} />
      </Field>
      <Field span={6}>
        <Label>Email Address <RequiredMark>*</RequiredMark></Label>
        <Input type="email" name="adminEmail" value={formData.adminEmail} onChange={handleChange} placeholder="admin@company.com" />
        <Helper>This email receives the OTP and is required for admin login verification.</Helper>
      </Field>
      <Field span={6}>
        <Label>Username <RequiredMark>*</RequiredMark></Label>
        <Input value={adminUsername} readOnly placeholder="Auto-filled from admin email" />
        <Helper>Username is auto-filled from email. No extra entry is needed.</Helper>
      </Field>
      <Field span={6}>
        <Label>Mobile Number</Label>
        <Input name="adminMobile" value={formData.adminMobile} onChange={handleChange} />
      </Field>
      <Field span={6}>
        <Label>Password <RequiredMark>*</RequiredMark></Label>
        <Input
          type={pwVisible ? 'text' : 'password'}
          name="adminPassword"
          value={formData.adminPassword}
          onChange={handleChange}
          placeholder="Minimum 8 characters"
        />
        <Helper>Password should be at least 8 characters.</Helper>
      </Field>
      <Field span={12}>
        <Button type="button" onClick={() => setPwVisible((prev) => !prev)}>
          {pwVisible ? 'Hide Password' : 'Show Password'}
        </Button>
      </Field>

      <SectionHeading>
        <SectionTitle>Referral gate</SectionTitle>
        <SectionText>Organization creation stays locked until the approved referral code is entered correctly.</SectionText>
      </SectionHeading>

      <Field span={12}>
        <ReferralPanel valid={referralCodeValid}>
          <Label>Referral Code <RequiredMark>*</RequiredMark></Label>
          <Input
            name="referralCode"
            value={formData.referralCode}
            onChange={handleChange}
            placeholder="Enter referral code"
            invalid={Boolean(formData.referralCode) && !referralCodeValid}
          />
          <ReferralStatus valid={referralCodeValid}>
            {referralCodeValid
              ? 'Referral code accepted. Organization creation is enabled.'
              : 'Use the exact referral code to activate the Create Organization button.'}
          </ReferralStatus>
        </ReferralPanel>
      </Field>
    </SectionGrid>
  );

  const renderStep4 = () => (
    <VerifyWrap>
      <VerifyBadge>OTP</VerifyBadge>
      <VerifyTitle>Verify admin email</VerifyTitle>
      <VerifyText>
        The organization has been created and an OTP was sent to <strong>{formData.adminEmail}</strong>.
        The admin account will not be allowed to log in until this email is verified.
      </VerifyText>
      <Field style={{ width: '100%', maxWidth: 360 }}>
        <Label>Verification OTP <RequiredMark>*</RequiredMark></Label>
        <Input
          value={otpCode}
          onChange={(event) => setOtpCode(event.target.value)}
          placeholder="Enter 6 digit OTP"
          maxLength={6}
          style={{ textAlign: 'center', fontSize: 22, letterSpacing: '0.22em' }}
        />
      </Field>
      <Button primary type="button" onClick={handleVerifyOtp} disabled={loading}>
        {loading ? 'Verifying...' : 'Verify Email And Continue'}
      </Button>
    </VerifyWrap>
  );

  return (
    <PageShell>
      <Layout>
        <SidePanel>
          <SideEyebrow>Zilverra onboarding</SideEyebrow>
          <SideTitle>Create your organization with confidence.</SideTitle>
          <SideText>
            This flow now protects organization creation with a referral gate, auto-fills the admin username from email,
            and requires admin email verification before the first login.
          </SideText>

          <StepRail>
            {steps.map((item, index) => {
              const current = index + 1;
              return (
                <StepCard key={item.title} active={step === current} done={step > current}>
                  <StepMeta>
                    <StepIndex active={step === current} done={step > current}>{current}</StepIndex>
                    <StepTitle>{item.title}</StepTitle>
                  </StepMeta>
                  <StepDesc>{item.description}</StepDesc>
                </StepCard>
              );
            })}
          </StepRail>

          <SummaryCard>
            <SummaryTitle>Live summary</SummaryTitle>
            <SummaryValue>
              <div><strong>Organization:</strong> {formData.orgName || 'Not entered yet'}</div>
              <div><strong>Admin email:</strong> {formData.adminEmail || 'Not entered yet'}</div>
              <div><strong>Username:</strong> {adminUsername || 'Will fill from email'}</div>
              <div><strong>Referral:</strong> {referralCodeValid ? 'Approved' : 'Pending approval'}</div>
            </SummaryValue>
          </SummaryCard>
        </SidePanel>

        <MainPanel>
          <Header>
            <div>
              <HeaderTitle>Organization registration</HeaderTitle>
              <HeaderText>
                Complete the required fields, review the admin access details, and enter the approved referral code to unlock
                organization creation.
              </HeaderText>
            </div>
            <MandatoryNote>`*` marks mandatory fields.</MandatoryNote>
          </Header>

          {error ? <ErrorBanner>{error}</ErrorBanner> : null}
          {step === 4 ? (
            <SuccessBanner>
              Organization record created successfully. Verify the admin email to activate admin login access.
            </SuccessBanner>
          ) : null}

          {step === 1 && renderStep1()}
          {step === 2 && renderStep2()}
          {step === 3 && renderStep3()}
          {step === 4 && renderStep4()}

          {step < 4 ? (
            <ActionBar>
              <ActionNote>
                {step === 3
                  ? 'Create Organization remains disabled until the referral code is valid.'
                  : 'Move step by step. Required fields must be completed before continuing.'}
              </ActionNote>
              <ButtonRow>
                {step > 1 ? (
                  <Button type="button" onClick={() => { setError(''); setStep((prev) => prev - 1); }}>
                    Back
                  </Button>
                ) : null}
                {step < 3 ? (
                  <Button primary type="button" onClick={goToNextStep}>
                    Continue
                  </Button>
                ) : (
                  <Button primary type="button" onClick={handleRegister} disabled={loading || !referralCodeValid}>
                    {loading ? 'Creating organization...' : 'Create Organization'}
                  </Button>
                )}
              </ButtonRow>
            </ActionBar>
          ) : null}
        </MainPanel>
      </Layout>
    </PageShell>
  );
};

export default SetupWizard;
